import React, { useCallback, useEffect, useRef } from 'react';
import { TopicServiceCard } from './TopicServiceCard';
import { buildOrderedTopicCards, type CmsTopicSections } from '../utils/resolveQuickSection';

const TILE_ORDER = ['buy-vehicle', 'sell-vehicle', 'financing', 'trade-in'] as const;

export interface VehicleMarketplaceQuickSectionProps {
  onSelect: (categoryId: string, slug: string) => void;
  topicSections: CmsTopicSections;
}

export const VehicleMarketplaceQuickSection: React.FC<VehicleMarketplaceQuickSectionProps> = ({
  onSelect,
  topicSections,
}) => {
  const items = buildOrderedTopicCards('vehicle', TILE_ORDER, topicSections);
  const scrollerRef = useRef<HTMLDivElement>(null);

  const updateCardScales = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const focusX = scrollerRect.left + scrollerRect.width * 0.42;
    const cards = scroller.querySelectorAll<HTMLElement>('.vehicle-market-carousel__card');

    cards.forEach((card) => {
      const rect = card.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      const delta = (center - focusX) / Math.max(scrollerRect.width, 1);
      const distance = Math.min(1, Math.abs(delta));
      const fromRight = delta > 0;
      const shrink = fromRight ? 0.12 : 0.06;
      const scale = 1 - distance * shrink;

      card.style.transform = `scale(${scale})`;
      card.style.opacity = String(1 - distance * 0.08);
    });
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateCardScales();
      });
    };

    updateCardScales();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      scroller.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [items.length, updateCardScales]);

  if (items.length === 0) return null;

  return (
    <section
      id="home-section-vehicle"
      className="legal-quick-section legal-quick-section--vehicle"
      aria-labelledby="quick-section-title-vehicle"
    >
      <h2 id="quick-section-title-vehicle" className="legal-quick-section__title">
        Vehicle Marketplace
      </h2>
      <div ref={scrollerRef} className="catalog-grid vehicle-market-carousel">
        {items.map((item, index) => (
          <div key={`${item.categoryId}-${item.slug}`} className="catalog-grid-item">
            <div className="vehicle-market-carousel__card">
              <TopicServiceCard item={item} index={index} onSelect={onSelect} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
